/*
 * Clinician-facing copy for an AI suggestion that could not be applied, plus
 * the extraction of what the suggestion would have written.
 *
 * These are deliberately NOT the strings `resolveOperation` and
 * `validateSuggestionContent` return. Those go back to the model so it can
 * retry — they name the operation type and the offending field on purpose — and
 * are useless to a clinician, who saw "insertAfterText: text ... not found in
 * document" in the dropped list. The reasons here are derived from the op
 * itself, so both the reveal-time and the accept-time drop paths can produce
 * identical copy without either one parsing a technical message.
 *
 * Pure: only a type import, so `conditionMentionSideEffects` can depend on it
 * without closing an import cycle back through the editor registry.
 */
import type { EditorOperation } from '@seen/ai-agents'

/*
 * The closing clause every clinical-validity refusal ends with. It doubles as
 * the seam that tells an already-clinician-facing reason apart from a technical
 * one on the accept path: the accept pipeline forwards a validator's reason and
 * a resolve failure's reason through the SAME callback and marks neither, so
 * the host has nothing else to discriminate on.
 */
const CONDITION_REASON_SUFFIX = ", so it can't be added to the note."

export function conditionMissingDropReason(display?: string): string {
  return `${display ?? 'This condition'} is no longer in the chart${CONDITION_REASON_SUFFIX}`
}

export function conditionInactiveDropReason(display?: string): string {
  return `${display ?? 'This condition'} is no longer active${CONDITION_REASON_SUFFIX}`
}

export function conditionNotOnChartDropReason(display?: string): string {
  return `${display ?? 'This condition'} is not on this participant's chart${CONDITION_REASON_SUFFIX}`
}

export function isConditionDropReason(reason: string): boolean {
  return reason.endsWith(CONDITION_REASON_SUFFIX)
}

// The suggestion's content itself is the problem: it uses something this note's
// editor can't render. Reachable on a record staged before a content feature was
// turned off, not only on malformed AI output.
export const CONTENT_UNSUPPORTED_DROP_REASON =
  "This suggestion uses formatting the note can't show, so it can't be added."

// No usable operation on the record — a suggestion staged by a newer version of
// the app than this one, so there is nothing specific to say.
export const UNAPPLIABLE_DROP_REASON =
  'This suggestion can no longer be applied to the note.'

const ANCHOR_MAX_LENGTH = 60

function quoted(text: string): string {
  const trimmed = text.trim()
  return `"${trimmed.length > ANCHOR_MAX_LENGTH ? `${trimmed.slice(0, ANCHOR_MAX_LENGTH)}…` : trimmed}"`
}

/*
 * Which way an anchor failed to resolve, produced by `findUniqueTextRange` and
 * consumed by the copy below. The two are genuinely different situations for a
 * clinician: an ambiguous anchor is text they can see, plainly, more than once,
 * and telling them it "couldn't be matched" reads as a broken product.
 */
export type AnchorFailureKind = 'ambiguous' | 'notFound'

/*
 * Why a suggestion had nowhere to go, stated from the op's own anchor.
 *
 * `anchorFailure` says which way the anchor failed, re-asked against the live
 * document by the caller (see `clinicianDropReason`) — the op alone cannot tell
 * "the note no longer contains this" from "the note contains this twice". It is
 * undefined for an op that doesn't anchor on text at all and for a `replaceNode`
 * whose anchor resolved fine but whose FIELD didn't, which is why the ambiguity
 * wording is opt-in per case rather than a blanket rewrite.
 */
export function anchorDropReason(
  op: EditorOperation,
  anchorFailure: AnchorFailureKind | undefined
): string {
  const isAmbiguous = anchorFailure === 'ambiguous'
  switch (op.type) {
    case 'replaceText':
      return isAmbiguous
        ? `The text this was meant to replace (${quoted(op.findText)}) appears more than once in the note, so it wasn't clear which one to change.`
        : `The text this was meant to replace (${quoted(op.findText)}) couldn't be found in the note, so nothing was changed.`
    case 'insertAfterText':
      return isAmbiguous
        ? `The text this was meant to follow (${quoted(op.anchorText)}) appears more than once in the note, so it wasn't clear where to add it.`
        : `The text this was meant to follow (${quoted(op.anchorText)}) couldn't be found in the note, so there was nowhere to add it.`
    case 'insertBeforeText':
      return isAmbiguous
        ? `The text this was meant to come before (${quoted(op.anchorText)}) appears more than once in the note, so it wasn't clear where to add it.`
        : `The text this was meant to come before (${quoted(op.anchorText)}) couldn't be found in the note, so there was nowhere to add it.`
    case 'replaceNode':
      return isAmbiguous
        ? `The text marking the field this was meant to update (${quoted(op.anchorText)}) appears more than once in the note, so it wasn't clear which field to change.`
        : `The field this was meant to update couldn't be found after ${quoted(op.anchorText)} in the note.`
    /* The three ops below fail without a discriminator (their matchers report
     * an untyped error, unlike the text anchors re-asked above), so the copy
     * must stay accurate across every cause — a missing target reads the same
     * here as a duplicated one or a value the field doesn't offer. Hedged
     * wording over a confident falsehood: telling a clinician a section "is no
     * longer in the note" while they can see it twice reads as a broken
     * product. */
    case 'appendToSection':
      return `A single section called ${quoted(op.headingText)} couldn't be identified in the note — it may be missing or appear more than once — so there was nowhere to add this.`
    case 'setChoiceById':
      return `The field this was meant to set to ${quoted(op.value)} couldn't be updated — it may no longer be in the note, or ${quoted(op.value)} may not be one of its options.`
    case 'setTaskItemChecked':
      return `A single checklist item ${quoted(op.labelText)} couldn't be identified in the note — it may be missing or appear in more than one section — so it wasn't ${op.checked ? 'checked' : 'unchecked'}.`
    case 'insertAtEnd':
    case 'replaceAll':
      // Neither anchors anywhere, so reaching here means the content was refused.
      return CONTENT_UNSUPPORTED_DROP_REASON
    /* A revision is persisted as the op it replaced, with the prose swapped, so
     * a dropped record never carries this type — the drop paths read persisted
     * ops. It is listed only because the union it switches on is the LIVE tool
     * union, which does carry it. */
    case 'reviseProposal':
      return CONTENT_UNSUPPORTED_DROP_REASON
  }
}

/*
 * The clinician-facing reason for a suggestion that couldn't be placed.
 * `contentIsUnsupported` and `anchorFailure` are both the caller's own re-runs
 * of a gate this module cannot run: the failure kinds are indistinguishable once
 * the technical message is discarded, and only the caller holds the editor
 * needed to re-ask.
 */
export function unplaceableDropReason(
  op: EditorOperation | null,
  contentIsUnsupported: boolean,
  anchorFailure: AnchorFailureKind | undefined
): string {
  if (!op) {
    return UNAPPLIABLE_DROP_REASON
  }
  return contentIsUnsupported
    ? CONTENT_UNSUPPORTED_DROP_REASON
    : anchorDropReason(op, anchorFailure)
}

/*
 * The HTML a suggestion would write into the note, read off the op. Undefined
 * for an op that writes no content of its own (a checkbox toggle) — the caller
 * then has only the placement label to show.
 */
export function suggestionOpContentHtml(
  op: EditorOperation | null
): string | undefined {
  if (!op) {
    return undefined
  }
  switch (op.type) {
    case 'replaceText':
    case 'replaceNode':
      return op.newContent
    case 'insertAfterText':
    case 'insertBeforeText':
    case 'appendToSection':
    case 'insertAtEnd':
    case 'replaceAll':
      return op.content
    case 'setChoiceById':
      return op.value
    case 'reviseProposal':
      return op.content
    case 'setTaskItemChecked':
      return undefined
  }
}
